import { useCallback } from 'react';
import { DateTime } from 'luxon';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { modalSlice } from '../store/reducers/ModalSlice';
import { AccountTypes, GlobalRouteNames, LocalStorageItems, ModalVariants, SuccessRouteNames } from '../models/Common';
import { paymentsApi } from '../services/apiServices/user/payments';
import {
  handleServicePaymentActivateAnalytics,
  handleServicePaymentSuspensionAnalytics,
  getErrorMessage,
} from '../utils';
import { useCustomSnackbar } from './useCustomEnqueueSnackbar';
import { OrderParams, PreparedCalculateData } from '../models/Calculate';
import { TransformServicePreset } from '../models/ServicePresets';

export const useServicePaymentActions = ({
  params,
  calculation,
  init,
  accountType,
  promoBannerId,
}: {
  params: OrderParams;
  calculation: PreparedCalculateData | null;
  init: TransformServicePreset | undefined;
  accountType: AccountTypes | '';
  promoBannerId?: number;
}) => {
  const dispatch = useDispatch();
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const { openModal } = modalSlice.actions;
  const history = useHistory();
  const [serviceActivateAPI, { isLoading: isLoadingActivate }] = paymentsApi.useServicePaymentMutation();

  const handleActivate = useCallback(async () => {
    try {
      if (!calculation) return;
      await serviceActivateAPI({ ...params, ...(promoBannerId ? { promoBannerId: promoBannerId.toString() } : {}) })
        .unwrap()
        .then(() => {
          localStorage.setItem(LocalStorageItems.ServiceName, init?.serviceForPayment.Name || '');
          localStorage.setItem(
            LocalStorageItems.FromDate,
            DateTime.fromSeconds(calculation.view.fts).setZone('Europe/Kiev').toJSDate().toString(),
          );
          localStorage.setItem(
            LocalStorageItems.ToDate,
            DateTime.fromSeconds(calculation.view.tts).setZone('Europe/Kiev').toJSDate().toString(),
          );
          if (init?.isPaidServiceSuspension) {
            handleServicePaymentSuspensionAnalytics(
              init?.serviceForPayment.AnalyticsID ?? init?.serviceForPayment.Name,
              params.count || 1,
              calculation.amount,
            );
            history.push(`${GlobalRouteNames.SuccessPage}${SuccessRouteNames.ServiceSuspendedSuccessfulPage}`);
          } else {
            handleServicePaymentActivateAnalytics(
              (init?.serviceForPayment?.AnalyticsID ??
                init?.serviceForPayment?.AnalyticsID ??
                init?.serviceForPayment?.Name) ||
                '',
              params.count || 1,
              calculation.amount,
              null,
            );
            history.push(`${GlobalRouteNames.SuccessPage}${SuccessRouteNames.ServiceActivationSuccessfulPage}`);
          }
        });
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
    }
  }, [
    calculation,
    customEnqueueSnackbar,
    history,
    init?.isPaidServiceSuspension,
    init?.serviceForPayment?.AnalyticsID,
    init?.serviceForPayment.Name,
    params,
    promoBannerId,
    serviceActivateAPI,
  ]);

  const handlePay = useCallback(() => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.PaymentTypeMethods,
        content: {
          service: init?.serviceForPayment.Name,
          ...(calculation && { currentCalculation: calculation }),
          paramsForCalculation: params,
          userId: promoBannerId,
        },
      }),
    );
  }, [calculation, dispatch, init?.serviceForPayment.Name, promoBannerId, openModal, params]);

  const handleRestore = useCallback(() => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.RestoreCallback,
        content: {
          service: init?.serviceForPayment.Name,
          accountType,
        },
      }),
    );
  }, [accountType, dispatch, init?.serviceForPayment.Name, openModal]);
  return { handleActivate, handlePay, handleRestore, isLoadingActivate };
};
